/**
 * ToolbarUrlInput — inline URL input bar for the TipTap toolbar.
 * Used by Link, Image, and Embed buttons.
 * When type is 'image', also shows an Upload button that uploads to S3
 * and auto-fills the URL (G7-2).
 */

import { useState, useRef } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { apiClient } from '@/core/api/client';

type ToolbarUrlInputProps = {
  type: 'link' | 'image' | 'embed';
  value: string;
  onChange: (value: string) => void;
  onApply: (urlOverride?: string) => void;
  onCancel: () => void;
};

const PLACEHOLDERS: Record<string, string> = {
  link: 'https://example.com',
  image: 'Paste URL or upload a file →',
  embed: 'YouTube, Vimeo, or Spotify URL',
};

export function ToolbarUrlInput({ type, value, onChange, onApply, onCancel }: ToolbarUrlInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.publicUrl;
      onChange(url);
      onApply(url);
    } catch {
      onChange('');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2 w-full px-3 py-2 border-t border-gray-200 bg-white">
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={PLACEHOLDERS[type]}
        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#026857] focus:border-[#026857]"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onApply(); }
          if (e.key === 'Escape') onCancel();
        }}
      />

      {type === 'image' && (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            aria-label="Upload image"
          >
            {isUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileUpload}
            className="hidden"
          />
        </>
      )}

      <button
        type="button"
        onClick={() => onApply()}
        className="px-3 py-1.5 text-xs bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors"
      >
        Apply
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}
